import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FormField } from '@/components/shared/form-field';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';

export interface ExpenseExternalParty {
  id: string;
  name: string;
  isActive: boolean;
}

export interface ExpenseExternalPartyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  party?: ExpenseExternalParty | null;
  onSaved?: (party: ExpenseExternalParty) => void;
}

export const ExpenseExternalPartyDialog = ({
  open,
  onOpenChange,
  party,
  onSaved,
}: ExpenseExternalPartyDialogProps) => {
  const { t } = useTranslation('spending');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const editing = Boolean(party);

  useEffect(() => {
    if (!open) return;
    setName(party?.name ?? '');
    setIsActive(party?.isActive ?? true);
  }, [open, party]);

  const save = useMutation({
    mutationFn: () =>
      editing && party
        ? api.patch<ExpenseExternalParty>(`/expenses/external-parties/${party.id}`, {
            name: name.trim(),
            isActive,
          })
        : api.post<ExpenseExternalParty>('/expenses/external-parties', {
            name: name.trim(),
          }),
    onSuccess: async (saved) => {
      toast.success(t('externalParties.saved'));
      onOpenChange(false);
      setName('');
      setIsActive(true);
      await queryClient.invalidateQueries({ queryKey: ['expenses', 'external-parties'] });
      onSaved?.(saved);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {editing ? t('externalParties.edit') : t('externalParties.create')}
          </DialogTitle>
          <DialogDescription>
            {editing ? t('externalParties.editDescription') : t('externalParties.createDescription')}
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            if (!name.trim()) return;
            save.mutate();
          }}
        >
          <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
            <FormField label={t('externalParties.name')} required>
              {(field) => (
                <Input
                  id={field.id}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  required
                  autoFocus
                  aria-invalid={field.invalid}
                  aria-describedby={field['aria-describedby']}
                />
              )}
            </FormField>
            {editing ? (
              <SwitchField
                id="expense-external-party-active"
                label={t('externalParties.active')}
                description={t('externalParties.activeHint')}
                checked={isActive}
                onCheckedChange={setIsActive}
              />
            ) : null}
          </DialogBody>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('action.cancel')}
            </Button>
            <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
              {t('externalParties.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
